import { Injectable } from '@angular/core';
import { IFiContactData } from './fi-contacts';
import { IUserContactUniqueData } from '../fi-auth-shared/fi-authenticate-shared.service';
import { IContactModel } from '../models/common';
import { ContactService } from './contact.service';
import { FiCustomValidatorPatterns } from '../fi-custom-validator-patterns/fi-custom-validator-patterns';
import { Observable, Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { IUserModel } from '@fi-shared/models/user.model';
import { FiContactInformationTypes } from '@fi-shared/types/fi-contact-information-types';

export enum FiUserContactDataItemType {
    Emails,
    PhoneNumbers,
}

@Injectable()
export class FiContactsService {
    private _contactUid: number = 10000;

    constructor(private contactService: ContactService) {}

    getContactUid(): number {
        return ++this._contactUid;
    }

    removeUidFromContacts(contacts: IContactModel[]): IContactModel[] {
        return contacts.map((contact: IContactModel) => {
            delete contact.Uid;
            return contact;
        });
    }

    getContactData(getItemsFn: () => any[]): IFiContactData {
        return {
            signInTextId: undefined,
            primaryTextId: 'common.primary',
            isSignInOptionShown: () => false,
            selectAsDefaultDisabled: (contact: IContactModel) =>
                this.contactService.isSelectAsDefaultDisabled(getItemsFn().length, contact),
            showSelectAsDefault: () => getItemsFn().length > 1,
            showSelectAsSignIn: () => false,
            isContactEditDeleteDisabled: (contact: IContactModel) => this.contactService.isContactImported(contact),
            onContactValueChanged: () => {},
            isPKSignInOptionDisabled: () => false,
        };
    }

    getUserContactData(args: {
        user: IUserModel;
        itemType: FiUserContactDataItemType;
        contactUniqueData: IUserContactUniqueData[];
        isPKUser: boolean;
        onDestroy$: Observable<void>;
    }): IFiContactData {
        let getItemsFn: () => IContactModel[];
        let signInTextId: string;
        let primaryTextId: string = 'common.primary';
        const contactValueChanged$ = new Subject<IContactModel>();
        args.onDestroy$.subscribe(() => {
            contactValueChanged$.complete();
        });
        contactValueChanged$.pipe(debounceTime(500)).subscribe((contact) => {
            // note: we need to validate SignIn contacts -> https://chainformation.atlassian.net/browse/FI-5855
            // https://chainformation.atlassian.net/browse/FI-5253
            // if (contact.IsSignIn) {
            //     return;
            // }

            const contactUniqueData: IUserContactUniqueData[] = args.contactUniqueData;

            const contactValue = this.getContactValueForValidation(contact.ContactValue, contact.ExtendedValue);
            // when user manually changes email - we should check if it is unique and add to userEmailUniqueData
            const userContactUniqueItem = contactUniqueData.find(
                (item) => item.ContactValue?.toLowerCase() === contactValue,
            );
            if (!contactValue || userContactUniqueItem) return;

            let isContactValueValid = false;
            if (this.contactService.isEmailType(contact.ContactType)) {
                const emailRegex = new RegExp(FiCustomValidatorPatterns.Email);
                isContactValueValid = emailRegex.test(contactValue);
            } else if (this.contactService.isPhoneNumberType(contact.ContactType)) {
                const phoneNumberRegex = new RegExp(FiCustomValidatorPatterns.PhoneNumberWithoutCountryCode);
                isContactValueValid = phoneNumberRegex.test(contact.ContactValue.toString());
            }

            if (isContactValueValid) {
                this.contactService.isContactUnique(args.user.Id, contactValue).then((result) => {
                    contactUniqueData.push(result);
                });
            }
        });

        if (args.itemType === FiUserContactDataItemType.Emails) {
            getItemsFn = () => args.user.EmailAddressContacts;
            signInTextId = 'login.sign_in_email';
            primaryTextId = 'common.primary_email';
        } else if (args.itemType === FiUserContactDataItemType.PhoneNumbers) {
            getItemsFn = () => args.user.PhoneNumberContacts;
            signInTextId = 'auth.sign_in_number';
        } else {
            throw `unknown item type "${args.itemType}" in FiUserContactData`;
        }

        return {
            signInTextId: signInTextId,
            primaryTextId: primaryTextId,
            isSignInOptionShown: () => true,
            // PK user should have "is default" options disabled
            showSelectAsDefault: (contact: IContactModel) => {
                return (
                    !!getItemsFn()?.length /*&& !args.isPKUser */ &&
                    !this.contactService.isPhoneNumberType(contact.ContactType)
                );
            },
            selectAsDefaultDisabled: (contact: IContactModel) => {
                return this.contactService.isSelectAsDefaultDisabled(getItemsFn().length, contact);
            },
            isPKSignInOptionDisabled: () => args.isPKUser,
            showSelectAsSignIn: (contact: IContactModel) => {
                if (args.isPKUser) return contact.IsSignIn;

                const selectAsSignInDisabledFn = () => {
                    return !!args.contactUniqueData.find((item: IUserContactUniqueData) => {
                        return (
                            item.ContactValue?.toLowerCase() ===
                                this.getContactValueForValidation(contact.ContactValue, contact.ExtendedValue) &&
                            !item.IsUnique
                        );
                    });
                };
                return !selectAsSignInDisabledFn();
            },
            isContactEditDeleteDisabled: (contact: IContactModel) => {
                // PK user can't edit sign in contact
                return (args.isPKUser && contact.IsSignIn) || this.contactService.isContactImported(contact);
            },
            onContactValueChanged: (contact: IContactModel) => {
                contactValueChanged$.next(contact);
            },
        };
    }

    getFullContactValue(contact: IContactModel): string {
        return this.getFullContactValueByProps(contact.ContactType, contact.ContactValue, contact.ExtendedValue);
    }

    getFullContactValueByProps(
        contactType: FiContactInformationTypes,
        contactValue: string,
        extendedValue: string,
    ): string {
        let fullContactValue: string = '';
        if (this.contactService.isEmailType(contactType) && contactValue) {
            fullContactValue = contactValue;
        } else if (this.contactService.isPhoneNumberType(contactType) && contactValue) {
            fullContactValue = `(${extendedValue}) ${contactValue}`;
        }
        return fullContactValue.toLowerCase();
    }

    getContactValueForValidation(controlValue: string, prefixValue: string): string {
        if (!prefixValue?.length) return controlValue?.toLowerCase();

        const value: string = controlValue?.startsWith('0') ? controlValue.substring(1) : controlValue;
        return `(${prefixValue}) ${value}`;
    }
}
